/**
 * Option lists a tenant can rename under Admin > Loan Fields & Properties.
 *
 * Deliberately a leaf with no imports: `loan.constants` reaches back into the config barrel through
 * the admin utils, so anything placed there cannot be loaded on its own.
 */

type RenamableOption = { key?: string; title?: string; visible?: boolean }

/** Fields whose option titles are the tenant's to rename. The stored value is always the key. */
export const editableOptionFields: string[] = [
  'productType',
  'propertyType',
  'proposedOccupancy',
  'borrowerType',
  'lienType',
  'condoType',
]

/** Dropdown labels for a renamed option list. Keys stay the stored values; titles are what the user sees. */
export const customOptionLabels = (
  options?: RenamableOption[] | null,
  onlyVisible = true,
): Record<string, string> | null => {
  if (!Array.isArray(options) || !options.length) return null
  const labels: Record<string, string> = {}
  options.forEach((opt) => {
    if (!opt?.key) return
    if (onlyVisible && opt.visible === false) return
    labels[opt.key] = opt.title || opt.key
  })
  return Object.keys(labels).length ? labels : null
}

/**
 * The option list a renamable field is configured with.
 *
 * Loan Structure is the master list and outranks a section's own, so one set of names and one set
 * of hidden options serve every section the field appears on. Each section used to carry its own
 * copy, which is how a tenant ended up renaming the same property type two different ways and
 * reading a different list on each screen. A section's own list is still used when Loan Structure
 * holds none, so a tenant that only ever configured that section keeps what it had.
 */
export const editableFieldOptions = <T extends RenamableOption>(
  fieldKey: string,
  sectionProperties?: Record<string, { options?: any } | undefined>,
  masterProperties?: Record<string, { options?: any } | undefined>,
): T[] | null => {
  if (!editableOptionFields.includes(fieldKey)) return null

  const masterOptions = masterProperties?.[fieldKey]?.options
  if (masterOptions?.length) return masterOptions as T[]

  const sectionOptions = sectionProperties?.[fieldKey]?.options
  return sectionOptions?.length ? (sectionOptions as T[]) : null
}
